// WelcomeParticulier.jsx
// Page de sélection de formule post-inscription — rôle particulier
// Appelée depuis Register après navigate('/welcome/particulier')

import { AnimatePresence, motion } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';
import { useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useAuth } from '../contexts/AuthContext';

// ─────────────────────────────────────────────────────────────────────────────
// DONNÉES
// ─────────────────────────────────────────────────────────────────────────────

const FORMULES = [
  {
    id:          'decouverte',
    nom:         'Découverte',
    prix:        '0 €',
    periode:     '',
    trial:       null,
    description: 'Pour explorer PESA à son rythme, sans engagement.',
    acces: [
      "Centre d'Ajustement — cas de base",
      'Parcours de découverte',
      'Débrief automatique',
    ],
    horsAcces: [
      'Ateliers avancés',
      'Tous les cas disponibles',
      'Export des traces',
    ],
    accent:   '#22d3ee',
    accentBg: 'rgba(34,211,238,0.06)',
    accentBorder: 'rgba(34,211,238,0.2)',
    cta: 'Commencer gratuitement',
    highlighted: false,
  },
  {
    id:          'parcours',
    nom:         'Parcours',
    prix:        '10 €',
    periode:     '/mois',
    trial:       30,
    description: 'Accès complet aux cas et ateliers. 30 jours gratuits.',
    acces: [
      'Tous les cas et ateliers disponibles',
      'Progression documentée',
      'Certifications individuelles',
      'Débrief complet avec signaux',
    ],
    horsAcces: [
      'Export des traces décisionnelles',
    ],
    accent:   '#f59e0b',
    accentBg: 'rgba(245,158,11,0.06)',
    accentBorder: 'rgba(245,158,11,0.25)',
    cta: "Démarrer l'essai gratuit",
    highlighted: true,
  },
  {
    id:          'praticien',
    nom:         'Praticien',
    prix:        '25 €',
    periode:     '/mois',
    trial:       30,
    description: 'Pour intégrer PESA dans sa pratique professionnelle. 30 jours gratuits.',
    acces: [
      'Tout Parcours inclus',
      'Ateliers avancés — corpus étendu',
      'Export des traces décisionnelles',
      'Accès prioritaire aux nouveaux cas',
    ],
    horsAcces: [],
    accent:   '#f97316',
    accentBg: 'rgba(249,115,22,0.06)',
    accentBorder: 'rgba(249,115,22,0.2)',
    cta: "Démarrer l'essai gratuit",
    highlighted: false,
  },
];

// ─────────────────────────────────────────────────────────────────────────────
// COMPOSANTS
// ─────────────────────────────────────────────────────────────────────────────

const ScanLine = () => (
  <motion.div
    animate={{ top: ['-2%', '102%'] }}
    transition={{ duration: 7, repeat: Infinity, ease: 'linear', repeatDelay: 3 }}
    style={{
      position: 'absolute', left: 0, right: 0, height: '1px',
      background: 'linear-gradient(90deg, transparent, rgba(34,211,238,0.3) 30%, rgba(245,158,11,0.4) 70%, transparent)',
      zIndex: 10, pointerEvents: 'none',
    }}
  />
);

// ─────────────────────────────────────────────────────────────────────────────
// PAGE PRINCIPALE
// ─────────────────────────────────────────────────────────────────────────────

export default function WelcomeParticulier() {
  const navigate          = useNavigate();
  const location          = useLocation();
  const { user, setUser } = useAuth();
  const [selected, setSelected]   = useState(null);
  const [loading, setLoading]     = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const formule = FORMULES.find(f => f.id === selected);

  const handleConfirm = async () => {
    if (!selected || loading) return;
    setLoading(true);

    setConfirmed(true);

    // Pas d'appel API — on passe le plan choisi en state
    // Il sera appliqué après le login
    setTimeout(() => navigate('/register-success', {
      replace: true,
      state: { 
        email: location.state?.email,  // récupéré depuis Register via useLocation
        plan: selected                  // plan choisi, appliqué après login
      }
    }), 1600);
  };

  // ── Écran de confirmation ──────────────────────────────────────────────────
  if (confirmed && formule) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#020617', fontFamily: 'monospace' }}>
        <motion.div
          initial={{ opacity: 0, scale: 0.96 }}
          animate={{ opacity: 1, scale: 1 }}
          style={{ textAlign: 'center', maxWidth: 420, padding: '0 1.5rem' }}
        >
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: 'spring', damping: 12, delay: 0.1 }}
            style={{
              width: 64, height: 64, margin: '0 auto 1.5rem',
              border: `1px solid ${formule.accentBorder}`,
              background: formule.accentBg,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'relative',
            }}
          >
            <span style={{ position: 'absolute', top: -1, left: -1, width: 10, height: 10, borderTop: '2px solid #22d3ee', borderLeft: '2px solid #22d3ee' }} />
            <span style={{ position: 'absolute', bottom: -1, right: -1, width: 10, height: 10, borderBottom: '2px solid #f59e0b', borderRight: '2px solid #f59e0b' }} />
            <Check size={26} style={{ color: formule.accent }} strokeWidth={2} />
          </motion.div>

          <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
            <p style={{ fontSize: '0.45rem', letterSpacing: '0.3em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.2)', marginBottom: '0.5rem' }}>
              Plan activé
            </p>
            <h2 style={{ fontSize: '1.4rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.02em', marginBottom: '0.5rem' }}>
              {formule.nom}
              {formule.trial && (
                <span style={{ fontSize: '0.8rem', color: formule.accent, marginLeft: '0.5rem' }}>
                  · {formule.trial}j gratuits
                </span>
              )}
            </h2>
            <p style={{ fontSize: '0.55rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.18)' }}>
              Redirection vers le tableau de bord...
            </p>
          </motion.div>
        </motion.div>
      </div>
    );
  }

  // ── Sélection de formule ───────────────────────────────────────────────────
  return (
    <div style={{
      minHeight: '100vh', background: '#020617',
      fontFamily: 'monospace', position: 'relative', overflow: 'hidden',
      display: 'flex', flexDirection: 'column',
    }}>
      <ToastContainer />

      {/* Fond */}
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', background: 'radial-gradient(circle at 30% 20%, rgba(34,211,238,0.04), transparent 45%), radial-gradient(circle at 70% 80%, rgba(245,158,11,0.04), transparent 45%)' }} />
      <div style={{ position: 'absolute', inset: 0, pointerEvents: 'none', backgroundImage: `linear-gradient(rgba(34,211,238,0.04) 1px, transparent 1px), linear-gradient(90deg, rgba(245,158,11,0.04) 1px, transparent 1px)`, backgroundSize: '80px 80px', maskImage: 'radial-gradient(circle at 50% 40%, black 30%, transparent 80%)', WebkitMaskImage: 'radial-gradient(circle at 50% 40%, black 30%, transparent 80%)' }} />

      {/* En-tête */}
      <header style={{ padding: '1.5rem 2rem', borderBottom: '1px solid rgba(255,255,255,0.04)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative' }}>
        <ScanLine />
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: '#22d3ee', boxShadow: '0 0 8px #22d3ee', animation: 'pulse 2s ease-in-out infinite' }} />
          <span style={{ fontSize: '0.55rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)' }}>
            PESACORE · Activation compte
          </span>
        </div>
        {user && (
          <span style={{ fontSize: '0.5rem', letterSpacing: '0.18em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.15)' }}>
            {user.prenom} {user.nom}
          </span>
        )}
      </header>

      {/* Corps */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '3rem 1.5rem 2rem', maxWidth: 960, margin: '0 auto', width: '100%' }}>

        {/* Titre */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
          style={{ textAlign: 'center', marginBottom: '3rem' }}
        >
          <p style={{ fontSize: '0.44rem', letterSpacing: '0.35em', textTransform: 'uppercase', color: 'rgba(34,211,238,0.5)', marginBottom: '0.75rem' }}>
            Votre espace est prêt
          </p>
          <h1 style={{ fontSize: 'clamp(1.6rem, 5vw, 2.4rem)', fontWeight: 700, color: '#fff', letterSpacing: '-0.03em', lineHeight: 1.15, marginBottom: '0.75rem' }}>
            Choisissez votre formule
          </h1>
          <p style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.7, fontStyle: 'italic', maxWidth: '38ch', margin: '0 auto' }}>
            Vous pouvez commencer gratuitement et évoluer à tout moment.
            Les formules avec essai ne nécessitent pas de carte bancaire aujourd'hui.
          </p>
        </motion.div>

        {/* Grille formules */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem', width: '100%', marginBottom: '2rem' }}>
          {FORMULES.map((f, i) => {
            const isSel = selected === f.id;
            return (
              <motion.div
                key={f.id}
                initial={{ opacity: 0, y: 14 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: i * 0.08, duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                onClick={() => setSelected(f.id)}
                style={{
                  position: 'relative', cursor: 'pointer',
                  border: `1px solid ${isSel ? f.accentBorder : 'rgba(255,255,255,0.06)'}`,
                  background: isSel ? f.accentBg : 'rgba(255,255,255,0.02)',
                  padding: '1.75rem 1.5rem',
                  transition: 'border-color 0.2s, background 0.2s, box-shadow 0.2s',
                  boxShadow: isSel ? `0 0 32px ${f.accent}18` : 'none',
                  outline: 'none',
                }}
              >
                {/* Coins HUD */}
                <span style={{ position: 'absolute', top: -1, left: -1, width: 8, height: 8, borderTop: `1.5px solid ${isSel ? f.accent : 'rgba(34,211,238,0.15)'}`, borderLeft: `1.5px solid ${isSel ? f.accent : 'rgba(34,211,238,0.15)'}`, transition: 'border-color 0.2s' }} />
                <span style={{ position: 'absolute', bottom: -1, right: -1, width: 8, height: 8, borderBottom: `1.5px solid ${isSel ? f.accent : 'rgba(245,158,11,0.15)'}`, borderRight: `1.5px solid ${isSel ? f.accent : 'rgba(245,158,11,0.15)'}`, transition: 'border-color 0.2s' }} />

                {/* Badge recommandé */}
                {f.highlighted && (
                  <div style={{ position: 'absolute', top: '0.75rem', right: '0.75rem', background: 'rgba(245,158,11,0.1)', border: '1px solid rgba(245,158,11,0.25)', padding: '0.15rem 0.55rem' }}>
                    <span style={{ fontSize: '0.38rem', letterSpacing: '0.25em', textTransform: 'uppercase', color: '#f59e0b' }}>Recommandé</span>
                  </div>
                )}

                {/* Check sélection */}
                <AnimatePresence>
                  {isSel && (
                    <motion.div
                      initial={{ opacity: 0, scale: 0.5 }}
                      animate={{ opacity: 1, scale: 1 }}
                      exit={{ opacity: 0, scale: 0.5 }}
                      style={{ position: 'absolute', top: '0.75rem', left: '0.75rem', width: 18, height: 18, background: f.accent, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                    >
                      <Check size={10} color="#020617" strokeWidth={3} />
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Nom + prix */}
                <div style={{ marginBottom: '1rem' }}>
                  <p style={{ fontSize: '0.42rem', letterSpacing: '0.28em', textTransform: 'uppercase', color: isSel ? f.accent : 'rgba(255,255,255,0.25)', marginBottom: '0.35rem', transition: 'color 0.2s' }}>
                    {f.id}
                  </p>
                  <p style={{ fontSize: '1.1rem', fontWeight: 700, color: '#fff', letterSpacing: '-0.01em', marginBottom: '0.5rem' }}>
                    {f.nom}
                  </p>
                  <div style={{ display: 'flex', alignItems: 'baseline', gap: '0.3rem', marginBottom: '0.5rem' }}>
                    <span style={{ fontSize: '1.6rem', fontWeight: 300, color: isSel ? '#fff' : 'rgba(255,255,255,0.7)', transition: 'color 0.2s' }}>
                      {f.prix}
                    </span>
                    <span style={{ fontSize: '0.5rem', letterSpacing: '0.15em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.3)' }}>
                      {f.periode}
                    </span>
                  </div>
                  {f.trial && (
                    <span style={{ fontSize: '0.44rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: isSel ? f.accent : 'rgba(255,255,255,0.2)', transition: 'color 0.2s' }}>
                      {f.trial} jours gratuits · sans CB
                    </span>
                  )}
                </div>

                <p style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.35)', lineHeight: 1.65, fontStyle: 'italic', marginBottom: '1.1rem' }}>
                  {f.description}
                </p>

                {/* Accès */}
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.4rem', marginBottom: f.horsAcces.length ? '0.85rem' : 0 }}>
                  {f.acces.map((a, ai) => (
                    <div key={ai} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                      <div style={{ width: 4, height: 4, borderRadius: '50%', background: isSel ? f.accent : 'rgba(255,255,255,0.2)', flexShrink: 0, marginTop: '0.35rem', transition: 'background 0.2s', boxShadow: isSel ? `0 0 4px ${f.accent}` : 'none' }} />
                      <span style={{ fontSize: '0.56rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.5 }}>{a}</span>
                    </div>
                  ))}
                </div>

                {/* Hors accès */}
                {f.horsAcces.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.35rem', paddingTop: '0.75rem', borderTop: '1px solid rgba(255,255,255,0.04)' }}>
                    {f.horsAcces.map((h, hi) => (
                      <div key={hi} style={{ display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                        <div style={{ width: 4, height: 4, borderRadius: '50%', background: 'rgba(255,255,255,0.08)', flexShrink: 0, marginTop: '0.35rem' }} />
                        <span style={{ fontSize: '0.54rem', color: 'rgba(255,255,255,0.18)', lineHeight: 1.5 }}>{h}</span>
                      </div>
                    ))}
                  </div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* CTA de confirmation */}
        <AnimatePresence>
          {selected && (
            <motion.div
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 8 }}
              style={{ width: '100%', maxWidth: 400, display: 'flex', flexDirection: 'column', gap: '0.75rem', alignItems: 'center' }}
            >
              <button
                onClick={handleConfirm}
                disabled={loading}
                style={{
                  width: '100%', padding: '1rem 1.5rem',
                  background: loading ? 'rgba(255,255,255,0.04)' : `linear-gradient(135deg, ${formule.accent}, ${formule.accent}bb)`,
                  border: 'none',
                  color: loading ? 'rgba(255,255,255,0.3)' : '#020617',
                  fontFamily: 'monospace', fontSize: '0.58rem', fontWeight: 700,
                  letterSpacing: '0.22em', textTransform: 'uppercase',
                  cursor: loading ? 'not-allowed' : 'pointer',
                  display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.6rem',
                  transition: 'all 0.2s',
                }}
              >
                {loading ? (
                  <>
                    <motion.span
                      animate={{ rotate: 360 }}
                      transition={{ duration: 1.2, repeat: Infinity, ease: 'linear' }}
                      style={{ display: 'inline-block', width: 12, height: 12, border: '1.5px solid rgba(255,255,255,0.15)', borderTop: '1.5px solid rgba(255,255,255,0.5)', borderRadius: '50%' }}
                    />
                    Activation en cours...
                  </>
                ) : (
                  <>
                    {formule?.cta}
                    <ArrowRight size={13} />
                  </>
                )}
              </button>

              {/* Note sans CB si trial */}
              {formule?.trial && (
                <p style={{ fontSize: '0.42rem', letterSpacing: '0.2em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.18)', textAlign: 'center' }}>
                  Aucune carte bancaire requise aujourd'hui · {formule.trial} jours gratuits
                </p>
              )}
            </motion.div>
          )}
        </AnimatePresence>

        {/* Skip — aller directement en Découverte */}
        <motion.button
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.5 }}
          onClick={() => { setSelected('decouverte'); setTimeout(handleConfirm, 50); }}
          style={{ marginTop: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', fontFamily: 'monospace', fontSize: '0.42rem', letterSpacing: '0.22em', textTransform: 'uppercase', color: 'rgba(255,255,255,0.12)', transition: 'color 0.2s' }}
          onMouseEnter={e => e.currentTarget.style.color = 'rgba(255,255,255,0.3)'}
          onMouseLeave={e => e.currentTarget.style.color = 'rgba(255,255,255,0.12)'}
        >
          Continuer en Découverte →
        </motion.button>

      </div>
    </div>
  );
}